import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  deleteDocument,
  fetchFile,
  getDocument,
  listAllDocuments,
  documentsTotal,
  listDocumentPage,
  listPartnerNames,
  updateDocument,
  uploadDocument,
  type DocumentInput,
  type DocumentRecord,
  type UploadInput,
} from "@/lib/api/documents";
import { listCategories } from "@/lib/api/categories";
import type { DocumentFilters, ListState } from "@/lib/documents/document-list";

export const documentKeys = {
  all: ["documents"] as const,
  page: (workspaceId: string, state: ListState) =>
    [...documentKeys.all, "page", workspaceId, state] as const,
  total: (workspaceId: string, filters: DocumentFilters) =>
    [...documentKeys.all, "total", workspaceId, filters] as const,
  partners: (workspaceId: string) => [...documentKeys.all, "partners", workspaceId] as const,
  every: (workspaceId: string) => [...documentKeys.all, "every", workspaceId] as const,
  detail: (id: string) => [...documentKeys.all, "detail", id] as const,
  categories: (workspaceId: string) => ["categories", workspaceId] as const,
};

/** One page of the Belegjournal; the previous page stays on screen while the next one loads. */
export function useDocumentPage(workspaceId: string | undefined, state: ListState) {
  return useQuery({
    queryKey: documentKeys.page(workspaceId ?? "", state),
    queryFn: () => listDocumentPage(workspaceId!, state),
    enabled: Boolean(workspaceId),
    placeholderData: keepPreviousData,
  });
}

/** The gross sum of everything the filters match, across all pages. */
export function useDocumentsTotal(workspaceId: string | undefined, filters: DocumentFilters) {
  return useQuery({
    queryKey: documentKeys.total(workspaceId ?? "", filters),
    queryFn: () => documentsTotal(workspaceId!, filters),
    enabled: Boolean(workspaceId),
    placeholderData: keepPreviousData,
  });
}

export function usePartnerNames(workspaceId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: documentKeys.partners(workspaceId ?? ""),
    queryFn: () => listPartnerNames(workspaceId!),
    enabled: Boolean(workspaceId) && enabled,
  });
}

/** Every document of the workspace - used by the dashboard and the reports. */
export function useAllDocuments(workspaceId: string | undefined) {
  return useQuery({
    queryKey: documentKeys.every(workspaceId ?? ""),
    queryFn: () => listAllDocuments(workspaceId!),
    enabled: Boolean(workspaceId),
  });
}

export function useDocument(id: string | undefined) {
  return useQuery({
    queryKey: documentKeys.detail(id ?? ""),
    queryFn: () => getDocument(id!),
    enabled: Boolean(id),
  });
}

export function useCategories(workspaceId: string | undefined) {
  return useQuery({
    queryKey: documentKeys.categories(workspaceId ?? ""),
    queryFn: () => listCategories(workspaceId!),
    enabled: Boolean(workspaceId),
  });
}

function useInvalidateDocuments() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: documentKeys.all });
    // A document change can settle or unsettle a card charge.
    void queryClient.invalidateQueries({ queryKey: ["banking"] });
  };
}

export function useUploadDocument() {
  const invalidate = useInvalidateDocuments();
  return useMutation({
    mutationFn: (input: UploadInput) => uploadDocument(input),
    onSuccess: invalidate,
  });
}

export function useUpdateDocument() {
  const invalidate = useInvalidateDocuments();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Parameters<typeof updateDocument>[1] }) =>
      updateDocument(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteDocument() {
  const invalidate = useInvalidateDocuments();
  return useMutation({ mutationFn: deleteDocument, onSuccess: invalidate });
}

export type { DocumentInput };

/**
 * A stored file as a blob URL for this page only. A copied link opens nowhere else, unlike the
 * file URL with its token, which anyone holding it could use for minutes (QA-0022).
 */
export function useFileUrl(
  document: DocumentRecord,
  fileName: string = document.file,
  enabled = true,
) {
  const {
    data: blob,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["document-file", document.id, fileName],
    queryFn: () => fetchFile(document, fileName),
    enabled: enabled && Boolean(fileName),
    // PocketBase gives every upload a new name, so a name always means the same bytes.
    staleTime: Infinity,
  });
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!blob) return;
    const created = URL.createObjectURL(blob);
    // The object URL is an external resource tied to the blob: made and revoked by this effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setUrl(created);
    return () => {
      URL.revokeObjectURL(created);
      setUrl(undefined);
    };
  }, [blob]);
  return { url, isPending: enabled && isPending && !isError, isError };
}
